/* eslint-disable */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

// Enhanced practice task data with blanks for user to fill in
const practiceTasksData = [
  {
    id: 1,
    title: "Create a Variable and Echo It",
    description: "Create a variable called NAME with your name and echo it with a greeting message.",
    code: `# Create a variable with your name
NAME="________"  # Fill in your name

# Echo a greeting with the variable
echo "Hello, $________!"  # Fill in the variable reference`,
    solutions: [
      {
        blank: "NAME=\"________\"",
        answer: "John"
      },
      {
        blank: "echo \"Hello, $________!\"",
        answer: "NAME"
      }
    ],
    hint: "Use your name for the variable value and 'NAME' (without the $ prefix) to reference it in the echo command."
  },
  {
    id: 2,
    title: "Create a Simple Conditional Statement",
    description: "Complete the conditional statement to check if a file exists.",
    code: `FILE="example.txt"

# Check if the file exists
if [ ________ "$FILE" ]; then
  echo "File exists"
else
  echo "File ________"
fi`,
    solutions: [
      {
        blank: "if [ ________ \"$FILE\" ]",
        answer: "-f"
      },
      {
        blank: "echo \"File ________\"",
        answer: "does not exist"
      }
    ],
    hint: "Use '-f' to check if a file exists and 'does not exist' to complete the message."
  },
  {
    id: 3,
    title: "Create a For Loop",
    description: "Complete the for loop to iterate through a range of numbers.",
    code: `# Loop through numbers 1 to 5
for i in ________; do
  echo "Number: $________"
done`,
    solutions: [
      {
        blank: "for i in ________",
        answer: "{1..5}"
      },
      {
        blank: "echo \"Number: $________\"",
        answer: "i"
      }
    ],
    hint: "Use '{1..5}' for the range and 'i' for the variable in the echo command."
  },
  {
    id: 4,
    title: "Create a Simple Function",
    description: "Complete the function that takes a parameter and echoes it.",
    code: `# Define the function
________ {
  echo "Hello, $________!"
}

# Call the function
greet "World"`,
    solutions: [
      {
        blank: "________ {",
        answer: "greet()"
      },
      {
        blank: "echo \"Hello, $________!\"",
        answer: "1"
      }
    ],
    hint: "Use 'greet()' to define a function and '1' to access the first parameter inside the function."
  },
  {
    id: 5,
    title: "Read File Line by Line",
    description: "Complete the while loop to print each line of 'file.txt' with its line number.",
    code: `# Read each line in file.txt
count=1
while IFS= read -r line; do
  echo "$count: $________"
  count=$((count + 1))
done < ________`,
    solutions: [
      {
        blank: "echo \"$count: $________\"",
        answer: "line"
      },
      {
        blank: "done < ________",
        answer: "file.txt"
      }
    ],
    hint: "Use the variable 'line' inside the echo and redirect 'file.txt' into the loop."
  },
  {
    id: 6,
    title: "Simple Case Statement",
    description: "Fill in the case statement to respond to 'y' or 'n' input.",
    code: `read -p "Continue? [y/n]: " ANSWER

case $ANSWER in
  y|Y) echo "________" ;;
  n|N) echo "________" ;;
  *) echo "Invalid option" ;;
esac`,
    solutions: [
      {
        blank: "y|Y) echo \"________\" ;;",
        answer: "Yes"
      },
      {
        blank: "n|N) echo \"________\" ;;",
        answer: "No"
      }
    ],
    hint: "Echo 'Yes' for y or Y and 'No' for n or N."
  }
];

function BashScriptSimulator() {
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [showSolution, setShowSolution] = useState(false);
  const [feedback, setFeedback] = useState("");

  const currentTask = practiceTasksData[currentTaskIndex];

  const handleAnswerChange = (blank, value) => {
    setUserAnswers({
      ...userAnswers,
      [blank]: value
    });
  };

  const checkAnswers = () => {
    const task = practiceTasksData[currentTaskIndex];
    const allCorrect = task.solutions.every(solution => 
      userAnswers[solution.blank]?.trim().toLowerCase() === solution.answer.toLowerCase()
    );

    if (allCorrect) {
      setFeedback("Correct! All answers are right.");
    } else {
      setFeedback("Some answers are incorrect. Try again or check the solution.");
    }
  };

  const resetTask = () => {
    setUserAnswers({});
    setShowSolution(false);
    setFeedback("");
  };

  const nextTask = () => {
    resetTask();
    setCurrentTaskIndex((currentTaskIndex + 1) % practiceTasksData.length);
  };

  const prevTask = () => {
    resetTask();
    setCurrentTaskIndex((currentTaskIndex - 1 + practiceTasksData.length) % practiceTasksData.length);
  };

  const renderCodeWithInputs = () => {
    let codeWithBlanks = currentTask.code;
    
    // Create a temporary div to hold the code
    const codeDiv = document.createElement('div');
    codeDiv.textContent = codeWithBlanks;
    
    // Replace blanks with input fields
    currentTask.solutions.forEach(solution => {
      const blank = solution.blank;
      
      // Create a simple display version to show instead of inputs
      codeWithBlanks = codeWithBlanks.replace(blank, `[BLANK: ${blank}]`);
    });

    return (
      <div>
        <pre 
          style={{ 
            whiteSpace: 'pre-wrap',
            fontFamily: 'monospace',
            padding: '15px',
            backgroundColor: '#282c34',
            color: '#abb2bf',
            borderRadius: '5px',
            lineHeight: '1.6'
          }}
        >
          {codeWithBlanks}
        </pre>
        
        <div style={{ marginTop: '20px' }}>
          <h3>Your Answers:</h3>
          {currentTask.solutions.map((solution, index) => (
            <div key={index} style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                For: <code>{solution.blank}</code>
              </label>
              <input
                type="text"
                value={userAnswers[solution.blank] || ''}
                onChange={(e) => handleAnswerChange(solution.blank, e.target.value)}
                style={{ 
                  width: '100%', 
                  padding: '8px', 
                  borderRadius: '4px',
                  border: '1px solid #ccc'
                }}
                placeholder="Type your answer here"
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ color: '#333', marginBottom: '20px' }}>Bash Script Syntax Simulator</h1>
      <p style={{ marginBottom: '30px' }}>Learn Bash scripting through examples and practice tasks.</p>
      
      <div style={{ 
        background: '#f5f5f5', 
        padding: '20px', 
        borderRadius: '8px',
        marginBottom: '30px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '15px'
        }}>
          <h2 style={{ margin: 0 }}>Task {currentTask.id}: {currentTask.title}</h2>
          <div>
            <button 
              onClick={prevTask}
              style={{ 
                marginRight: '10px',
                padding: '8px 15px',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Previous Task
            </button>
            <button 
              onClick={nextTask}
              style={{ 
                padding: '8px 15px',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Next Task
            </button>
          </div>
        </div>
        
        <p style={{ marginBottom: '15px' }}>{currentTask.description}</p>
        
        <div style={{ marginBottom: '20px' }}>
          {renderCodeWithInputs()}
        </div>
        
        <div style={{ marginBottom: '20px' }}>
          <button 
            onClick={checkAnswers}
            style={{ 
              marginRight: '10px',
              padding: '8px 15px',
              backgroundColor: '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Check Answers
          </button>
          <button 
            onClick={() => setShowSolution(!showSolution)}
            style={{ 
              marginRight: '10px',
              padding: '8px 15px',
              backgroundColor: showSolution ? '#f44336' : '#FF9800',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {showSolution ? "Hide Solution" : "Show Solution"}
          </button>
          <button 
            onClick={resetTask}
            style={{ 
              padding: '8px 15px',
              backgroundColor: '#607D8B',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Reset
          </button>
        </div>
        
        {feedback && (
          <div style={{ 
            padding: '15px', 
            backgroundColor: feedback.includes('Correct') ? '#e8f5e9' : '#ffebee',
            borderRadius: '4px',
            marginBottom: '20px',
            color: feedback.includes('Correct') ? '#2e7d32' : '#c62828'
          }}>
            {feedback}
          </div>
        )}
        
        {showSolution && (
          <div style={{ 
            padding: '15px', 
            backgroundColor: '#e3f2fd', 
            borderRadius: '4px',
            marginBottom: '20px'
          }}>
            <h3 style={{ marginTop: 0 }}>Solution:</h3>
            <ul>
              {currentTask.solutions.map((solution, index) => (
                <li key={index} style={{ marginBottom: '5px' }}>
                  <code style={{ 
                    backgroundColor: '#f1f8e9', 
                    padding: '3px 5px', 
                    borderRadius: '3px' 
                  }}>
                    {solution.blank}
                  </code>: 
                  <code style={{ 
                    backgroundColor: '#e8f5e9', 
                    padding: '3px 5px', 
                    borderRadius: '3px',
                    marginLeft: '5px' 
                  }}>
                    {solution.answer}
                  </code>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        <div style={{ 
          padding: '15px', 
          backgroundColor: '#fff3e0', 
          borderRadius: '4px' 
        }}>
          <h3 style={{ marginTop: 0 }}>Hint:</h3>
          <p style={{ margin: 0 }}>{currentTask.hint}</p>
        </div>
      </div>
      
      <Link to="/" style={{ 
        padding: '10px 20px', 
        backgroundColor: '#4CAF50', 
        color: 'white', 
        textDecoration: 'none', 
        borderRadius: '4px',
        display: 'inline-block'
      }}>
        Back to Home
      </Link>
    </div>
  );
}

export default BashScriptSimulator;
